/**
 * Feature Flags Store
 *
 * Holds the company-scoped feature flags evaluated by the backend and exposes
 * fail-closed accessors for components/composables.
 *
 * State:
 * - flags: Map of flag key -> evaluated FrontendFeatureFlag
 * - isLoading: Whether a load() is in progress
 * - loadedForCompanyId: The company the current flags were loaded for (staleness detection)
 *
 * Key features:
 * - Backend is the source of truth — the store only mirrors the response
 * - Fail-closed: unknown / not-yet-loaded flags resolve to `false` / `null`
 * - Persisted to localStorage for cross-tab sync (D9, FR-013) and fast hydration
 * - clearState() on logout, applyExternalState() for cross-tab reinjection
 *
 * See spec 030 — internal-contracts.md § 4.1.
 */

import type {
  FeatureFlagConfig,
  FrontendFeatureFlag,
} from '@openbox/shared-types/lib/featureFlags'
import { defineStore } from 'pinia'
import { useAuthStore } from '~/stores/auth'
import { useSecuredApi } from '~/composables/useSecuredApi'
import {
  clearFeatureFlagsState,
  readFeatureFlagsState,
  saveFeatureFlagsState,
  type PersistedFeatureFlagsState,
} from '~/utils/feature-flags.utils'

/** Response shape of `GET /auth/feature-flags`. */
type FeatureFlagsResponse = {
  data: FrontendFeatureFlag[]
}

/** Feature flags store state. */
export type FeatureFlagsState = {
  flags: Record<string, FrontendFeatureFlag>
  isLoading: boolean
  loadedForCompanyId: string | null
}

/**
 * Get the active company id from the auth store, if available.
 *
 * Defensive against missing Nuxt/Pinia context (e.g. unit tests) — returns null.
 */
function getActiveCompanyId(): string | null {
  try {
    const authStore = useAuthStore()
    return authStore.user?.workspace?.company?.id ?? null
  } catch {
    return null
  }
}

export const useFeatureFlagsStore = defineStore('featureFlags', {
  state: (): FeatureFlagsState => ({
    flags: {},
    isLoading: false,
    loadedForCompanyId: null,
  }),

  getters: {
    /**
     * Whether the state currently in the store actually belongs to the active
     * company. Hydrated localStorage state or cross-tab–synced state may carry a
     * `loadedForCompanyId` that does NOT match the active company — in that case
     * the store must behave as if nothing were loaded (fail-closed).
     */
    isLoadedForActiveCompany: (state): boolean => {
      const activeCompanyId = getActiveCompanyId()
      return (
        activeCompanyId !== null && state.loadedForCompanyId === activeCompanyId
      )
    },

    /**
     * Whether the given flag is enabled.
     *
     * Fail-closed: an unknown flag, a flag not yet loaded, or a flag loaded for
     * a DIFFERENT company than the active one all resolve to `false`.
     */
    isEnabled(): (key: string) => boolean {
      return (key: string): boolean => {
        if (!this.isLoadedForActiveCompany) {
          return false
        }
        return this.flags[key]?.enabled ?? false
      }
    },

    /**
     * The config object of the given flag, or `null` when the flag is unknown,
     * has no config, or was loaded for a different company than the active one.
     */
    getConfig(): (key: string) => FeatureFlagConfig | null {
      return (key: string): FeatureFlagConfig | null => {
        if (!this.isLoadedForActiveCompany) {
          return null
        }
        return this.flags[key]?.config ?? null
      }
    },
  },

  actions: {
    /**
     * Load the feature flags for the active company from the backend.
     *
     * Populates `flags`, records `loadedForCompanyId`, and persists the state to
     * localStorage for cross-tab sync. Idempotent — safe to call on every refetch
     * event (login / company switch / token refresh).
     */
    async load(): Promise<void> {
      const securedApi = useSecuredApi()
      // Clear potentially stale state up front: while the request is in flight
      // the store must not expose flags from a previous company / session
      // (fail-closed). `isLoading` keeps the composable returning `false`.
      this.flags = {}
      this.loadedForCompanyId = null
      this.isLoading = true
      try {
        const response =
          await securedApi<FeatureFlagsResponse>('/auth/feature-flags')
        const flags: Record<string, FrontendFeatureFlag> = {}
        for (const flag of response.data ?? []) {
          flags[flag.key] = flag
        }
        this.flags = flags
        this.loadedForCompanyId = getActiveCompanyId()
        saveFeatureFlagsState({
          flags: this.flags,
          loadedForCompanyId: this.loadedForCompanyId,
        })
      } catch (error) {
        // A failed refresh must not leave stale flags behind — keep fail-closed.
        this.flags = {}
        this.loadedForCompanyId = null
        throw error
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Clear the feature flags state and remove the persisted copy.
     *
     * Called on logout — the next authenticated session will reload from scratch.
     */
    clearState(): void {
      this.flags = {}
      this.loadedForCompanyId = null
      this.isLoading = false
      clearFeatureFlagsState()
    },

    /**
     * Reinject a state received from another tab via the `storage` event,
     * without re-calling the API (D9).
     *
     * Defensive: if the incoming state was loaded for a company that is NOT the
     * active one, it is discarded — the getters would fail-closed on it anyway,
     * but not applying it keeps the store from ever holding another company's
     * flags.
     */
    applyExternalState(state: PersistedFeatureFlagsState): void {
      const activeCompanyId = getActiveCompanyId()
      if (
        activeCompanyId !== null &&
        state.loadedForCompanyId !== activeCompanyId
      ) {
        return
      }
      this.flags = state.flags
      this.loadedForCompanyId = state.loadedForCompanyId
    },

    /**
     * Hydrate the store from the persisted localStorage copy, if any.
     *
     * Used on app boot to render flag-gated UI immediately without waiting for
     * the network round-trip. The plugin still calls `load()` to refresh.
     *
     * Defensive: persisted state loaded for a different company than the active
     * one is discarded (fail-closed) instead of being hydrated.
     */
    hydrateFromStorage(): void {
      const persisted = readFeatureFlagsState()
      if (!persisted) {
        return
      }
      const activeCompanyId = getActiveCompanyId()
      if (
        activeCompanyId !== null &&
        persisted.loadedForCompanyId !== activeCompanyId
      ) {
        return
      }
      this.flags = persisted.flags
      this.loadedForCompanyId = persisted.loadedForCompanyId
    },
  },
})
